import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn.js';
import { Button } from './Button.js';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Modal oyna.
 *
 * `<dialog>` emas, portal ishlatilgan: `<dialog>` ning brauzer bo'yicha
 * fokus xatti-harakati bir xil emas va Safari da `::backdrop` bosilganda
 * yopilmaydi.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Orqa fon aylanmasin — modal ochiqligi bilinib tursin
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Fokus modal ichiga o'tadi: klaviatura bilan yuruvchi foydalanuvchi
    // ortidagi sahifada adashib qolmasin.
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <button
        type="button"
        aria-label="Yopish"
        className="fixed inset-0 bg-n-900/40"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn('relative z-10 w-full bg-white outline-none', SIZES[size])}
      >
        <header className="flex items-start justify-between gap-4 border-b border-n-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-n-900">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-n-600">{description}</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Yopish">
            <X className="size-4" aria-hidden />
          </Button>
        </header>

        <div className="px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex justify-end gap-2 border-t border-n-200 px-5 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Tasdiqlash oynasi.
 *
 * O'chirish kabi qaytarib bo'lmaydigan amallar UCHUN MAJBURIY (TZ 4.4):
 * bir marta bosishda yo'qolgan ma'lumotni tiklash faqat savatchadan
 * yoki backupdan mumkin.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Tasdiqlash',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Bekor qilish
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-n-800">{message}</div>
    </Modal>
  );
}
